// src/pages/Admin/Recintos/Crear.jsx
import React, { useState, useEffect } from "react";
import {
  Card, Form, Input, Button, Select, InputNumber,
  Space, Divider, Typography, Row, Col, Alert
} from "antd";
import {
  SaveOutlined, CloseOutlined, HomeOutlined,
  EnvironmentOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import Recintos from "../../../../service/Recintos";
import { useNotify, useWithNotify } from "../../../../providers/NotificationProvider";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const COMUNAS_SANTIAGO = [
  'Cerrillos', 'Cerro Navia', 'Conchalí', 'El Bosque', 'Estación Central',
  'Huechuraba', 'Independencia', 'La Cisterna', 'La Florida', 'La Granja',
  'La Pintana', 'La Reina', 'Las Condes', 'Lo Barnechea', 'Lo Espejo',
  'Lo Prado', 'Macul', 'Maipú', 'Ñuñoa', 'Pedro Aguirre Cerda',
  'Peñalolén', 'Providencia', 'Pudahuel', 'Quilicura', 'Quinta Normal',
  'Recoleta', 'Renca', 'San Joaquín', 'San Miguel', 'San Ramón',
  'Santiago', 'Vitacura', 'Puente Alto', 'Pirque', 'San José de Maipo',
  'Colina', 'Lampa', 'Tiltil', 'San Bernardo', 'Buin',
  'Calera de Tango', 'Paine', 'Melipilla', 'Alhué', 'Curacaví',
  'María Pinto', 'San Pedro', 'Talagante', 'El Monte', 'Isla de Maipo',
  'Padre Hurtado', 'Peñaflor'
].sort();

export default function CrearRecinto() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const notify = useNotify();
  const withNotify = useWithNotify();



  const isEditing = !!id;

  useEffect(() => {
    if (isEditing) {
      loadRecinto();
    }
  }, [id]);

  const loadRecinto = async () => {
    try {
      setLoadingData(true);
      const response = await Recintos.GetById(id);
      form.setFieldsValue(response);
    } catch (error) {
      console.error("Error cargando recinto:", error);
      notify.error("No se pudo cargar el recinto");
      navigate("/admin/recintos");
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      if (isEditing) {
        await withNotify(
          Recintos.Update(id, values),
          {
            loading: "Actualizando recinto...",
            success: "Recinto actualizado exitosamente",
            error: "No se pudo actualizar el recinto"
          }
        );
      } else {
        await withNotify(
          Recintos.Create(values),
          {
            loading: "Creando recinto...",
            success: "Recinto creado exitosamente",
            error: "No se pudo crear el recinto"
          }
        );
      }

      navigate("/admin/recintos");

    } catch (error) {
      console.error("Error guardando recinto:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="p-6">
        <Card loading={true} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card>
        <div className="mb-6">
          <Title level={2} className="!mb-2">
            <HomeOutlined className="mr-3" />
            {isEditing ? "Editar Recinto" : "Crear Nuevo Recinto"}
          </Title>
          <Text type="secondary">
            {isEditing
              ? "Modifica los datos del recinto deportivo"
              : "Completa los datos para registrar un nuevo recinto deportivo"
            }
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            estado: "activo",
            tipo: "polideportivo"
          }}
        >
          <Divider orientation="left">Información Básica</Divider>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="nombre"
                label="Nombre del Recinto"
                rules={[
                  { required: true, message: "El nombre es requerido" },
                  { min: 3, message: "Mínimo 3 caracteres" }
                ]}
                hasFeedback
              >
                <Input
                  prefix={<HomeOutlined />}
                  placeholder="Ej: Polideportivo Ñuñoa"
                  size="large"
                  maxLength={100}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="tipo"
                label="Tipo de Recinto"
                rules={[{ required: true, message: "El tipo es requerido" }]}
                hasFeedback
              >
                <Select placeholder="Selecciona el tipo" size="large">
                  <Option value="calle_avenida">Calle o Avenida</Option>
                  <Option value="cancha">Cancha</Option>
                  <Option value="colegio_liceo">Colegio o Liceo</Option>
                  <Option value="gimnasio">Gimnasio</Option>
                  <Option value="multicancha">Multicancha</Option>
                  <Option value="otro">Otro</Option>
                  <Option value="parque">Parque</Option>
                  <Option value="piscina">Piscina</Option>
                  <Option value="pista_atletica">Pista Atlética</Option>
                  <Option value="plaza">Plaza</Option>
                  <Option value="polideportivo">Polideportivo</Option>
                  <Option value="sala">Sala</Option>
                  <Option value="sede_vecinal">Sede Vecinal</Option>
                  <Option value="skate_park">Skate Park</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Ubicación</Divider>

          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                name="direccion"
                label="Dirección"
                rules={[
                  { required: true, message: "La dirección es requerida" },
                  { min: 5, message: "Mínimo 5 caracteres" }
                ]}
                hasFeedback
              >
                <Input
                  prefix={<EnvironmentOutlined />}
                  placeholder="Calle y número"
                  size="large"
                  maxLength={200}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="comuna"
                label="Comuna"
                rules={[{ required: true, message: "La comuna es requerida" }]}
                hasFeedback
              >
                <Select
                  placeholder="Selecciona la comuna"
                  size="large"
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                    if (!option?.children) return false;
                    return option.children.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {COMUNAS_SANTIAGO.map((comuna) => (
                    <Option key={comuna} value={comuna}>
                      {comuna}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Detalles</Divider>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="estado"
                label="Estado"
                rules={[{ required: true, message: "El estado es requerido" }]}
              >
                <Select placeholder="Selecciona el estado" size="large">
                  <Option value="activo">Activo</Option>
                  <Option value="inactivo">Inactivo</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="descripcion"
            label="Descripción"
            rules={[
              { max: 500, message: "Máximo 500 caracteres" }
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Descripción del recinto, instalaciones disponibles, etc."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Divider />

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              size="large"
            >
              {isEditing ? "Actualizar Recinto" : "Crear Recinto"}
            </Button>
            <Button
              icon={<CloseOutlined />}
              onClick={() => navigate("/admin/recintos")}
              size="large"
            >
              Cancelar
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
}
